// Resolution rule for the Agents board's `?focus=<reviewItemId>` deep links.
//
// The board is reached by navigation (from Calls & Meetings, the COAC register,
// a composer workbench), so it mounts while `useReviewQueue`'s fetch is still in
// flight. An empty `items` list therefore means one of two opposite things —
// "not loaded yet" or "genuinely not pending" — and the board must not treat the
// first as the second (that is what left the deep link opening a bare kanban).
//
// Pure so the three-way decision is testable without a router, a query client or
// a drawer: WAIT keeps the intent latched, OPEN applies it, ABANDON gives up and
// tells the user why.

import type { ReviewQueueItem } from './use-review-queue';

export type ReviewFocusResolution =
    /** The queue cannot answer yet — hold the intent, do not clear the URL. */
    | { readonly outcome: 'wait' }
    /** The item is in the queue — focus it (and open the drawer for panel kinds). */
    | { readonly outcome: 'open'; readonly item: ReviewQueueItem }
    /** The queue has answered and the item is not in it — it was reviewed already. */
    | { readonly outcome: 'abandon' };

/**
 * Decide what a pending `?focus=` intent should do against the current queue.
 *
 * @param focusId  The review-item id the deep link asked for.
 * @param items    The review queue as currently known to the client.
 * @param settled  Has the pending-artefact fetch resolved at least once?
 */
export function resolveReviewFocus(
    focusId: string,
    items: ReadonlyArray<ReviewQueueItem>,
    settled: boolean,
): ReviewFocusResolution {
    const item = items.find((it) => it.id === focusId);
    if (item) return { outcome: 'open', item };
    return settled ? { outcome: 'abandon' } : { outcome: 'wait' };
}
